import { type AppLocale, LOCALES, type MeDto, type ReferralDto } from '@spin/shared';
import { useEffect, useState } from 'react';
import { http } from '../api/http';
import { logout } from '../boot';
import { Avatar } from '../components/common';
import { CloseIcon, GmCoin } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { tg } from '../tg/bridge';

const LANGS: AppLocale[] = ['en', 'ru', 'az', 'tr', 'uz'];

/** The language pills at the top of every portal page. */
export function LanguagePills() {
  const me = useSession((s) => s.me);
  const [busy, setBusy] = useState(false);
  const pick = async (locale: AppLocale) => {
    if (!me || me.locale === locale) return;
    setBusy(true);
    try {
      useSession.getState().setMe(await http.patch<MeDto>('/me', { locale }));
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lang-pills" role="group" aria-label="Language">
      {LANGS.filter((l) => (LOCALES as readonly string[]).includes(l)).map((l) => (
        <button
          key={l}
          className={me?.locale === l ? 'on' : ''}
          disabled={busy}
          onClick={() => void pick(l)}
          data-testid={`lang-${l}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const HEART =
  'M0 15C-4 12.2-16 5.6-16-4.2-16-10.6-11.6-14-7.2-14-3.6-14-1.2-11.8 0-9.2 1.2-11.8 3.6-14 7.2-14 11.6-14 16-10.6 16-4.2 16 5.6 4 12.2 0 15Z';

/** A chubby glossy heart centred on (x, y), `size` wide, turned by `turn` degrees. */
function GlossyHeart({ x, y, size, turn }: { x: number; y: number; size: number; turn: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${turn}) scale(${size / 32})`}>
      <path d={HEART} fill="url(#start-heart)" />
      <path
        d="M-12-6C-12-10-8.6-11.6-6-11 -3.6-10.4-3.4-8-5.6-7-8-6-9.8-4.4-10.6-2.6-11.4-1-12-3-12-6Z"
        fill="#fff"
        opacity="0.38"
      />
    </g>
  );
}

/** One arrow carved into the table: a curved band ending in a head. */
const ARROW =
  'M44 21.5C33 22.5 25 28 20.6 37.6L13 34.8 16.6 52.6 30.4 41.2 23.4 40.4C27 33 33.6 28.8 44.4 28Z';
/** The bottle drawn upright round (0, 0): mouth at the top, 92 long. */
const BOTTLE =
  'M-4.4-41-4.8-19C-5-11-14-8.6-14 1.4L-14 42.4Q-14 46-10.4 46L10.4 46Q14 46 14 42.4L14 1.4C14-8.6 5-11 4.8-19L4.4-41Z';

/** The game's tile: a green glass bottle on the wooden table, two carved arrows round it and glossy hearts. */
function StartArt() {
  return (
    <svg className="portal-art start-art" viewBox="0 0 100 100" aria-hidden>
      <defs>
        <radialGradient id="start-heart" cx="0.38" cy="0.3" r="0.75">
          <stop offset="0" stopColor="#ff8456" />
          <stop offset="0.55" stopColor="#f6491f" />
          <stop offset="1" stopColor="#d22d10" />
        </radialGradient>
        <linearGradient id="start-glass" gradientUnits="userSpaceOnUse" x1="-14" x2="14" y1="0" y2="0">
          <stop offset="0" stopColor="#7fb62f" />
          <stop offset="0.22" stopColor="#c2de4c" />
          <stop offset="0.5" stopColor="#a6cf3c" />
          <stop offset="0.82" stopColor="#6aa424" />
          <stop offset="1" stopColor="#457f15" />
        </linearGradient>
        <linearGradient id="start-rim" x1="0" x2="1">
          <stop offset="0" stopColor="#4f8e1d" />
          <stop offset="0.35" stopColor="#8fc23e" />
          <stop offset="1" stopColor="#3a7012" />
        </linearGradient>
        <radialGradient id="start-edge" cx="0.5" cy="0.5" r="0.62">
          <stop offset="0.55" stopColor="#5a2808" stopOpacity="0" />
          <stop offset="1" stopColor="#5a2808" stopOpacity="0.4" />
        </radialGradient>
        <filter id="start-blur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>
      <rect x="-4" y="-4" width="108" height="108" fill="url(#start-edge)" />
      {[0, 180].map((turn) => (
        <g key={turn} transform={`rotate(${turn} 50 50) translate(50 50) scale(1.1) translate(-50 -50)`}>
          <path d={ARROW} fill="rgba(255, 228, 190, 0.45)" transform="translate(0.7 0.8)" />
          <path d={ARROW} fill="rgba(112, 52, 12, 0.5)" transform="translate(-0.5 -0.6)" />
          <path d={ARROW} fill="#c0773a" opacity="0.62" />
        </g>
      ))}
      <g transform="translate(46.5 49) rotate(21) scale(1.06)">
        <path
          d={BOTTLE}
          fill="rgba(70, 28, 0, 0.42)"
          transform="translate(3.5 3)"
          filter="url(#start-blur)"
        />
        <path d={BOTTLE} fill="url(#start-glass)" stroke="#3f7a14" strokeWidth="0.7" />
        <path
          d="M-13.6 35C-7 30.4-2.6 39.6 3.8 35.6 8 33 11 32.6 13.6 34L13.6 42.4Q13.6 45.6 10.4 45.6L-10.4 45.6Q-13.6 45.6-13.6 42.4Z"
          fill="#cfe97a"
          opacity="0.75"
        />
        <path d="M-13.4 44.2H13.4" stroke="#3f7a14" strokeWidth="2.6" strokeLinecap="round" />
        <ellipse
          cx="-7.4"
          cy="-2.6"
          rx="2.6"
          ry="6"
          transform="rotate(26 -7.4 -2.6)"
          fill="#fff"
          opacity="0.9"
        />
        <ellipse cx="-4" cy="4.6" rx="1.1" ry="1.3" fill="#fff" opacity="0.8" />
        <rect x="-11.2" y="8" width="2.4" height="24" rx="1.1" fill="#fff" opacity="0.32" />
        <rect x="-3" y="-38" width="1.3" height="19" rx="0.65" fill="#fff" opacity="0.5" />
        <rect
          x="-5.4"
          y="-46"
          width="10.8"
          height="5.6"
          rx="1.6"
          fill="url(#start-rim)"
          stroke="#346a10"
          strokeWidth="0.5"
        />
        <ellipse cx="0" cy="-46" rx="5.4" ry="1.5" fill="#6aa42a" />
        <ellipse cx="0" cy="-46" rx="3.6" ry="0.9" fill="#244d0b" />
      </g>
      <g style={{ filter: 'drop-shadow(0 1.5px 1.5px rgba(90, 30, 0, 0.35))' }}>
        <GlossyHeart x={44} y={13} size={18} turn={-14} />
        <GlossyHeart x={73} y={41} size={23} turn={10} />
        <GlossyHeart x={25} y={67} size={27} turn={-10} />
      </g>
    </svg>
  );
}

/** Live streams: a play button over silver "LIVE" letters on red, darker towards the bottom. */
function WatchArt() {
  return (
    <svg className="portal-art watch-art" viewBox="0 0 104 100" aria-hidden>
      <defs>
        <linearGradient id="watch-shade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a0014" stopOpacity="0" />
          <stop offset="1" stopColor="#3a0014" stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="watch-silver" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f2f2f6" />
          <stop offset="0.5" stopColor="#c4c4ce" />
          <stop offset="0.52" stopColor="#8e8e9c" />
          <stop offset="1" stopColor="#c9c9d3" />
        </linearGradient>
        <linearGradient id="watch-play" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#c8c2ee" />
        </linearGradient>
      </defs>
      <rect y="52" width="104" height="48" fill="url(#watch-shade)" />
      <rect x="18" y="10" width="68" height="44" rx="8" fill="rgba(255, 255, 255, 0.3)" />
      <path
        d="M45 22.5V41.5L61 32Z"
        fill="url(#watch-play)"
        stroke="url(#watch-play)"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <text
        x="52"
        y="89"
        textAnchor="middle"
        fontSize="31"
        fontWeight="900"
        fontFamily="'Arial Black', Arial, sans-serif"
        letterSpacing="1"
        fill="url(#watch-silver)"
        stroke="#5c5c6c"
        strokeWidth="0.7"
      >
        LIVE
      </text>
    </svg>
  );
}

function StatusEditor({ value, onClose }: { value: string | null; onClose: () => void }) {
  const t = useT();
  const [text, setText] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      useSession.getState().setMe(await http.put<MeDto>('/me/status', { text: text.trim() }));
      onClose();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dark-modal-layer" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dark-modal" role="dialog" data-testid="status-editor">
        <button className="dark-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <CloseIcon size={14} />
        </button>
        <b>{t('portal.status_title')}</b>
        <textarea
          className="dark-input"
          rows={3}
          maxLength={140}
          value={text}
          placeholder={t('portal.status_placeholder')}
          onChange={(e) => setText(e.target.value)}
          data-testid="status-input"
        />
        <button
          className="portal-btn green"
          disabled={busy}
          onClick={() => void save()}
          data-testid="status-save"
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

/**
 * The portal (the reference's welcome page, reached by the exit icon): languages, the player with GM and Logout,
 * Services, invited users, the status line, Start (back to the game) and Watch (live streams and domino).
 */
export function Portal() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const config = useSession((s) => s.config);
  const go = useSession((s) => s.go);
  const reset = useSession((s) => s.reset);
  const [invited, setInvited] = useState<number | null>(null);
  const [status, setStatus] = useState(false);
  const [license, setLicense] = useState(false);

  useEffect(() => {
    http
      .get<ReferralDto>('/referral')
      .then((r) => setInvited(r.invited))
      .catch(() => undefined);
  }, []);

  const support = config?.support;
  return (
    <div className="portal" data-testid="portal">
      <div className="portal-page">
        <LanguagePills />
        <div className="portal-head">
          <span className="portal-avatar">
            <Avatar name={me.displayName} photoUrl={me.photoUrl} size="100%" />
          </span>
          <b className="portal-user ellipsis">{me.username ?? me.displayName}</b>
          <span className="gm-pill" data-testid="portal-gm">
            <GmCoin size={18} />
            {me.gm}
          </span>
          <button className="logout" onClick={logout} data-testid="logout">
            {t('portal.logout')}
          </button>
        </div>
        <button
          className="portal-card services"
          onClick={() => go({ name: 'services' })}
          data-testid="open-services"
        >
          <b>{t('portal.services')}</b>
          <span className="green-badge">2</span>
        </button>
        <button
          className="portal-card invited"
          onClick={() => go({ name: 'invited' })}
          data-testid="open-invited"
        >
          <span>🎁 {t('portal.invited')}</span>
          <span className="count-circle">{invited ?? 0}</span>
        </button>
        <div className="portal-card status" data-testid="status-line">
          {me.statusText ?? `${t('portal.no_status')} ✍️`}
        </div>
        <div className="status-actions">
          <button onClick={() => setStatus(true)} aria-label={t('portal.status_title')}>
            •••
          </button>
          <button className="plus" onClick={() => setStatus(true)} aria-label={t('portal.status_title')}>
            +
          </button>
        </div>
        <div className="portal-tiles">
          <button
            className="portal-tile start"
            onClick={() => reset({ name: 'table' })}
            data-testid="portal-start"
          >
            <StartArt />
            <span className="tile-label">{t('portal.start')}</span>
          </button>
          <button
            className="portal-tile watch"
            onClick={() => go({ name: 'watch' })}
            data-testid="portal-watch"
          >
            <WatchArt />
            <span className="tile-label">{t('portal.watch')}</span>
          </button>
        </div>
      </div>
      <footer className="portal-foot">
        <span>
          © {config?.brand.year ?? 2024} {config?.brand.name ?? t('app.title')}
        </span>
        <div className="foot-links">
          {support?.telegram && (
            <button onClick={() => tg.openTelegramUrl(support.telegram!)}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <circle cx="12" cy="12" r="11" fill="#2aa3e0" />
                <path
                  d="m5.5 11.6 11.6-4.5c.6-.2 1 .1.8.9l-2 9.4c-.1.6-.6.8-1.1.5l-3-2.2-1.5 1.4c-.2.2-.3.3-.6.3l.2-3.1 5.6-5c.2-.2 0-.3-.4-.1L8.3 13.6l-3-.9c-.6-.2-.6-.6.2-1.1Z"
                  fill="#fff"
                />
              </svg>
              Telegram
            </button>
          )}
          {support?.whatsapp && (
            <button onClick={() => tg.openTelegramUrl(support.whatsapp!)}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <circle cx="12" cy="12" r="11" fill="#25d366" />
                <path
                  d="M8.5 7.5c.3-.3.7-.3.9 0l1 1.6c.2.3.1.6-.1.9l-.5.6c.6 1.3 1.6 2.3 2.9 2.9l.6-.5c.3-.2.6-.3.9-.1l1.6 1c.3.2.3.6 0 .9-1 1-2.4 1.2-3.7.5a9 9 0 0 1-4.2-4.2c-.7-1.3-.5-2.7.6-3.6Z"
                  fill="#fff"
                />
              </svg>
              WhatsApp
            </button>
          )}
        </div>
      </footer>
      <button className="license-btn" onClick={() => setLicense(true)} data-testid="license">
        {t('portal.license')}
      </button>
      {status && <StatusEditor value={me.statusText} onClose={() => setStatus(false)} />}
      {license && (
        <div className="dark-modal-layer" onClick={(e) => e.target === e.currentTarget && setLicense(false)}>
          <div className="dark-modal license" role="dialog" data-testid="license-dialog">
            <button
              className="dark-modal-close"
              onClick={() => setLicense(false)}
              aria-label={t('common.close')}
            >
              <CloseIcon size={14} />
            </button>
            <b>{t('portal.license_title', { name: config?.brand.name ?? t('app.title') })}</b>
            {support?.email && <span className="license-mail">📩 {support.email}</span>}
            <p>{t('portal.license_text', { year: config?.brand.year ?? 2024 })}</p>
            <p className="license-credits">{t('portal.credits')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
